/**
 * Reine Logik der oeffentlichen Teilnehmerseite (#/unterweisung/<linkToken>).
 *
 * Kein React, kein Supabase: Schrittfolge, sessionStorage-Schluessel und
 * -Serialisierung, Antwort-Umschalten, Lesefreigabe. Alles, was die Seite an
 * Entscheidungen trifft, steht hier und ist in teilnahmeSeiteLogic.test.ts
 * abgedeckt — die Komponenten bleiben duenn (kein jsdom im Test).
 *
 * Bewusst NICHT hier: Formularpruefung und Fortschritt in Prozent — die
 * liegen in services/sifa/teilnahmeLogic.ts (gemeinsam mit dem Innendienst).
 */
import type {
  TeilnahmeAntwort,
  TeilnahmeErgebnis,
  TestAuswertung,
  UnterweisungAbschnitt,
  UnterweisungFrage,
} from '../../../services/sifa/types';

/**
 * Echtbetrieb oder Testlauf (#/unterweisung-test/<token>, Nr. 144). Im Test
 * gibt es keine Sitzung in der Datenbank (kein starteTeilnahme), keinen
 * Abschluss, kein Zertifikat — nur die Auswertung, die nichts schreibt.
 */
export type TeilnahmeModus = 'echt' | 'test';

/**
 * Was der Testlauf vom Ergebnis zeigt und speichert — wie im Echtbetrieb nur
 * Punkte, Grenze und bestanden; dazu das Rest-Kontingent des Links. Bewusst
 * KEINE Liste falscher Fragen: zusammen mit einem zweiten Durchlauf verriete
 * sie die richtigen Antworten (Antwort-Orakel). Die zeigt der Editor.
 */
export type TestErgebnisAnzeige = Pick<TestAuswertung, 'punkte' | 'punkteMax' | 'bestanden' | 'bestehenProzent' | 'auswertungenRest'>;

/** Nimmt aus einer Auswertung nur die angezeigten Felder — alles andere kommt nie in Zustand oder Speicher. */
export const testErgebnisAus = (a: TestErgebnisAnzeige): TestErgebnisAnzeige => ({
  punkte: a.punkte,
  punkteMax: a.punkteMax,
  bestanden: a.bestanden,
  bestehenProzent: a.bestehenProzent,
  auswertungenRest: a.auswertungenRest ?? null,
});

export type SchrittArt = 'begruessung' | 'abschnitt' | 'frage' | 'formular' | 'unterschrift' | 'ergebnis';

/** Ein Schritt der Seite; `index` zaehlt Abschnitte bzw. Fragen ab 0. */
export interface Schritt {
  art: SchrittArt;
  index: number;
}

/** Mindestlesezeit je Abschnitt, bevor „Weiter" ohne Scroll-Ende freigegeben wird. */
export const MINDEST_LESEZEIT_MS = 10_000;
/** Polling-Takt der QR-Bruecke (wie QuoteEditor). */
export const QR_POLL_MS = 2_500;
/** Serverseitige Lebensdauer einer Signaturanfrage (sifa_signatur_anfragen.expires_at). */
export const QR_ABLAUF_MS = 10 * 60_000;
/** Groesste Breite der Unterschrift in Pixeln (sifa_teilnahmen.unterschrift). */
export const UNTERSCHRIFT_MAX_BREITE = 480;

/** Der Ausschnitt der Unterweisung, den die Schrittfolge braucht. */
export interface SchrittfolgeQuelle {
  inhalt: ReadonlyArray<Pick<UnterweisungAbschnitt, 'id'>>;
  fragen: ReadonlyArray<Pick<UnterweisungFrage, 'id' | 'abschnittId'>>;
}

/**
 * Die Reihenfolge (Vorbild: Paketformat der Tablet-App — ein Abschnitt, dann
 * die Fragen ZU DIESEM Abschnitt): Begruessung → je Abschnitt [Abschnitt,
 * dann seine Fragen] → die uebrigen Fragen (abschnittId null oder auf einen
 * Abschnitt, den es nicht gibt) → Formular → Unterschrift → Ergebnis.
 * `index` einer Frage ist ihre Position in `fragen` (die Seite schlaegt
 * damit nach), nicht ihre Position in der Folge. Innerhalb eines Abschnitts
 * und am Ende bleibt die Reihenfolge aus `fragen` erhalten. Ohne Abschnitte
 * oder ohne Fragen fallen die jeweiligen Schritte einfach weg.
 */
export const schrittfolge = (u: SchrittfolgeQuelle): Schritt[] => {
  const folge: Schritt[] = [{ art: 'begruessung', index: 0 }];
  const abschnitte = u.inhalt ?? [];
  const fragen = u.fragen ?? [];
  const ids = new Set(abschnitte.map((a) => a.id));
  const nachAbschnitt = (id: string): number[] =>
    fragen.flatMap((f, i) => ((f.abschnittId || '') === id ? [i] : []));
  abschnitte.forEach((a, i) => {
    folge.push({ art: 'abschnitt', index: i });
    for (const fi of nachAbschnitt(a.id)) folge.push({ art: 'frage', index: fi });
  });
  fragen.forEach((f, i) => {
    const ziel = f.abschnittId || '';
    if (!ziel || !ids.has(ziel)) folge.push({ art: 'frage', index: i });
  });
  folge.push({ art: 'formular', index: 0 }, { art: 'unterschrift', index: 0 }, { art: 'ergebnis', index: 0 });
  return folge;
};

/** Position des ersten Schritts einer Art in der Folge, -1 wenn es keinen gibt. */
export const ersterSchrittDerArt = (folge: Schritt[], art: SchrittArt): number =>
  folge.findIndex((s) => s.art === art);

/**
 * Laufende Nummer eines Frage-Schritts in der ANZEIGE („Frage 2 von 5"):
 * gezaehlt wird die Position unter den Frage-Schritten der Folge, nicht der
 * Index in `fragen` — bei Fragen je Abschnitt liefe der sonst durcheinander.
 * 0, wenn der Schritt keine Frage ist.
 */
export const frageNummerInFolge = (folge: Schritt[], schrittIndex: number): number => {
  if (folge[schrittIndex]?.art !== 'frage') return 0;
  let n = 0;
  for (let i = 0; i <= schrittIndex; i++) if (folge[i].art === 'frage') n += 1;
  return n;
};

/**
 * Video: Vorspulen ist nicht erlaubt. Wer weiter springt, als er gesehen
 * hat, wird auf die hoechste gesehene Zeit zurueckgesetzt; zurueckspulen
 * geht immer. Die Toleranz faengt das Ruckeln von `timeupdate` ab (der
 * Browser meldet die Zeit nur alle ~250 ms). Liefert die Zeit, auf die das
 * Video gesetzt werden soll, oder null, wenn nichts zu tun ist.
 */
export const videoSprungKorrektur = (gewuenscht: number, gesehenMax: number, toleranzS = 1): number | null => {
  if (!Number.isFinite(gewuenscht) || !Number.isFinite(gesehenMax)) return null;
  return gewuenscht > gesehenMax + toleranzS ? Math.max(0, gesehenMax) : null;
};

/**
 * Haelt einen gespeicherten Schrittzeiger innerhalb der Folge. Wird der Kurs
 * zwischen zwei Aufrufen gekuerzt, zeigt der alte Zeiger sonst ins Leere.
 */
export const schrittBegrenzen = (folge: Schritt[], schritt: number): number => {
  if (!folge.length) return 0;
  if (!Number.isFinite(schritt) || schritt < 0) return 0;
  return Math.min(Math.floor(schritt), folge.length - 1);
};

export interface TeilnahmeFormular {
  vorname: string;
  nachname: string;
  /**
   * ISO-Datum YYYY-MM-DD (input type=date). Beim Eintippen steht hier bis
   * zum vollstaendigen Datum der getippte Text (siehe tippDatumWert).
   */
  geburtsdatum: string;
  email: string;
}

/**
 * Alles, was ein Neuladen ueberleben muss. Liegt je linkToken im
 * sessionStorage; die Unterschrift selbst wird NICHT gespeichert (sie geht
 * sofort an die RPC).
 */
export interface TeilnahmeZustand {
  version: 1;
  /** Sitzungstoken der Durchfuehrung (sifa_teilnahme_starten); null vor dem Start. */
  teilnahmeToken: string | null;
  /** Zeiger in die Schrittfolge. */
  schritt: number;
  antworten: TeilnahmeAntwort[];
  formular: TeilnahmeFormular;
  /** Pflicht-Haekchen „Ich habe die Unterweisung verstanden …". */
  verstanden: boolean;
  ergebnis: TeilnahmeErgebnis | null;
  /** Der Zertifikat-Versand wurde erfolgreich angestossen (kein zweiter Aufruf nach Neuladen). */
  zertifikatAngefordert: boolean;
  /**
   * Nur im Testlauf: die Auswertung aus sifa_testlink_auswerten. Im
   * Echtbetrieb fehlt das Feld ganz (dort steht das Ergebnis in `ergebnis`).
   */
  testErgebnis?: TestErgebnisAnzeige | null;
}

export const LEERES_FORMULAR: TeilnahmeFormular = { vorname: '', nachname: '', geburtsdatum: '', email: '' };

export const LEERER_ZUSTAND: TeilnahmeZustand = {
  version: 1,
  teilnahmeToken: null,
  schritt: 0,
  antworten: [],
  formular: LEERES_FORMULAR,
  verstanden: false,
  ergebnis: null,
  zertifikatAngefordert: false,
};

/**
 * Im Testlauf ist das Formular schon mit „Test" vorbelegt — wer testet, will
 * den Ablauf sehen, nicht seinen Namen tippen. Geburtsdatum und E-Mail
 * bleiben leer, damit auch die Pruefung des Formulars mitgetestet wird.
 */
export const TEST_FORMULAR: TeilnahmeFormular = { vorname: 'Test', nachname: 'Test', geburtsdatum: '', email: '' };

/** Anfangszustand je Modus. */
export const startZustand = (modus: TeilnahmeModus): TeilnahmeZustand =>
  modus === 'test' ? { ...LEERER_ZUSTAND, formular: TEST_FORMULAR, testErgebnis: null } : LEERER_ZUSTAND;

/**
 * sessionStorage-Schluessel je Token und Modus. Der Testlauf hat einen
 * eigenen Praefix: ein Testzustand darf nie als echte Sitzung gelesen werden
 * (und umgekehrt), selbst wenn zwei Tokens zufaellig gleich aussaehen.
 */
export const sessionKey = (linkToken: string, modus: TeilnahmeModus = 'echt'): string =>
  modus === 'test' ? `sifa-teilnahme-test:${linkToken}` : `sifa-teilnahme:${linkToken}`;

export const serialisiereZustand = (zustand: TeilnahmeZustand): string => JSON.stringify(zustand);

const istString = (v: unknown): v is string => typeof v === 'string';

const normalisiereAntworten = (roh: unknown): TeilnahmeAntwort[] => {
  if (!Array.isArray(roh)) return [];
  const out: TeilnahmeAntwort[] = [];
  for (const a of roh) {
    if (!a || typeof a !== 'object') continue;
    const frageId = (a as { frageId?: unknown }).frageId;
    const antwortIds = (a as { antwortIds?: unknown }).antwortIds;
    if (!istString(frageId) || !Array.isArray(antwortIds)) continue;
    out.push({ frageId, antwortIds: antwortIds.filter(istString) });
  }
  return out;
};

const normalisiereErgebnis = (roh: unknown): TeilnahmeErgebnis | null => {
  if (!roh || typeof roh !== 'object') return null;
  const e = roh as Record<string, unknown>;
  if (!istString(e.teilnahmeId) || typeof e.bestanden !== 'boolean') return null;
  return {
    teilnahmeId: e.teilnahmeId,
    bestanden: e.bestanden,
    punkte: typeof e.punkte === 'number' ? e.punkte : 0,
    punkteMax: typeof e.punkteMax === 'number' ? e.punkteMax : 0,
    zertifikatNr: istString(e.zertifikatNr) ? e.zertifikatNr : null,
    gueltigBis: istString(e.gueltigBis) ? e.gueltigBis : null,
  };
};

const normalisiereTestErgebnis = (roh: unknown): TestErgebnisAnzeige | null => {
  if (!roh || typeof roh !== 'object') return null;
  const e = roh as Record<string, unknown>;
  if (typeof e.bestanden !== 'boolean') return null;
  const zahl = (v: unknown, standard: number) => (typeof v === 'number' && Number.isFinite(v) ? v : standard);
  return {
    punkte: zahl(e.punkte, 0),
    punkteMax: zahl(e.punkteMax, 0),
    bestanden: e.bestanden,
    bestehenProzent: zahl(e.bestehenProzent, 0),
    auswertungenRest: typeof e.auswertungenRest === 'number' && Number.isFinite(e.auswertungenRest) ? e.auswertungenRest : null,
  };
};

/**
 * Liest einen gespeicherten Zustand tolerant: kaputtes JSON, fremde Version
 * oder fehlende Felder ergeben null bzw. Standardwerte — die Seite startet
 * dann vorn, statt mit einem halben Zustand zu stolpern. Ohne Sitzungstoken
 * gibt es keinen Fortschritt, der Zeiger faellt auf 0 zurueck.
 *
 * Testlauf: es gibt nie einen Sitzungstoken, der Zeiger gilt trotzdem; ein
 * echtes Ergebnis oder ein Zertifikat-Merker werden verworfen — ein Test
 * fuehrt nie zu Zertifikat oder Versand, auch nicht ueber einen fremden Eintrag.
 */
export const parseZustand = (raw: string | null | undefined, modus: TeilnahmeModus = 'echt'): TeilnahmeZustand | null => {
  if (!raw) return null;
  let obj: unknown;
  try {
    obj = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!obj || typeof obj !== 'object') return null;
  const z = obj as Record<string, unknown>;
  if (z.version !== 1) return null;
  const test = modus === 'test';
  const teilnahmeToken = !test && istString(z.teilnahmeToken) && z.teilnahmeToken ? z.teilnahmeToken : null;
  const f = (z.formular && typeof z.formular === 'object' ? z.formular : {}) as Record<string, unknown>;
  const schritt =
    typeof z.schritt === 'number' && Number.isFinite(z.schritt) && (teilnahmeToken || test) ? Math.max(0, Math.floor(z.schritt)) : 0;
  const basis: TeilnahmeZustand = {
    version: 1,
    teilnahmeToken,
    schritt,
    antworten: normalisiereAntworten(z.antworten),
    formular: {
      vorname: istString(f.vorname) ? f.vorname : '',
      nachname: istString(f.nachname) ? f.nachname : '',
      geburtsdatum: istString(f.geburtsdatum) ? f.geburtsdatum : '',
      email: istString(f.email) ? f.email : '',
    },
    verstanden: z.verstanden === true,
    ergebnis: test ? null : normalisiereErgebnis(z.ergebnis),
    zertifikatAngefordert: !test && z.zertifikatAngefordert === true,
  };
  return test ? { ...basis, testErgebnis: normalisiereTestErgebnis(z.testErgebnis) } : basis;
};

/** Liegt fuer den Ergebnis-Schritt etwas vor? Im Test zaehlt die Auswertung, sonst das echte Ergebnis. */
export const ergebnisVorhanden = (zustand: TeilnahmeZustand, modus: TeilnahmeModus): boolean =>
  modus === 'test' ? !!zustand.testErgebnis : !!zustand.ergebnis;

/** Gewaehlte Antwort-IDs einer Frage (leer, wenn noch nichts angekreuzt). */
export const gewaehlteAntworten = (antworten: TeilnahmeAntwort[], frageId: string): string[] =>
  antworten.find((a) => a.frageId === frageId)?.antwortIds ?? [];

/**
 * Kreuzt eine Antwort an oder wieder ab (Mehrfachauswahl). Liefert immer
 * ein neues Array; eine Frage ohne verbleibende Kreuze bleibt als leerer
 * Eintrag stehen, damit die Reihenfolge der Fragen erhalten bleibt.
 */
export const antwortUmschalten = (
  antworten: TeilnahmeAntwort[],
  frageId: string,
  antwortId: string,
): TeilnahmeAntwort[] => {
  const vorhanden = antworten.find((a) => a.frageId === frageId);
  if (!vorhanden) return [...antworten, { frageId, antwortIds: [antwortId] }];
  const ids = vorhanden.antwortIds.includes(antwortId)
    ? vorhanden.antwortIds.filter((id) => id !== antwortId)
    : [...vorhanden.antwortIds, antwortId];
  return antworten.map((a) => (a.frageId === frageId ? { frageId, antwortIds: ids } : a));
};

/** Mindestens ein Kreuz gesetzt — mehr prueft der Client bewusst nicht. */
export const frageBeantwortet = (antworten: TeilnahmeAntwort[], frageId: string): boolean =>
  gewaehlteAntworten(antworten, frageId).length > 0;

export const alleFragenBeantwortet = (fragen: UnterweisungFrage[], antworten: TeilnahmeAntwort[]): boolean =>
  fragen.every((f) => frageBeantwortet(antworten, f.id));

/** Fliesstext in Absaetze: Leerzeile trennt, einzelne Umbrueche bleiben im Absatz. */
export const absaetze = (text: string | null | undefined): string[] =>
  (text ?? '')
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((a) => a.trim())
    .filter(Boolean);

/**
 * „Weiter" wird frei, sobald bis zum Ende gescrollt wurde ODER die
 * Mindestlesezeit um ist. Passt der Text ohne Scrollen auf den Bildschirm,
 * zaehlt nur die Zeit — sonst waere der Knopf bei kurzen Abschnitten sofort
 * frei und die Lesepflicht leer.
 */
export const leseFreigabe = (p: {
  ueberlauf: boolean;
  scrollEnde: boolean;
  verstricheneMs: number;
  mindestMs?: number;
}): boolean => {
  const mindest = p.mindestMs ?? MINDEST_LESEZEIT_MS;
  if (p.verstricheneMs >= mindest) return true;
  return p.ueberlauf && p.scrollEnde;
};

/** Handy-Seite fuer die QR-Bruecke: #/sign/<qrToken>?u=1 (u = Unterweisung, RPC sifa_submit_signature). */
export const signUrl = (origin: string, pathname: string, qrToken: string): string =>
  `${origin}${pathname}#/sign/${encodeURIComponent(qrToken)}?u=1`;

/**
 * Touch-Geraet = grober Zeiger ODER schmaler Bildschirm (useIsMobile). Ein
 * Tablet mit breitem Bildschirm unterschreibt trotzdem direkt, ein schmales
 * Browserfenster am PC bekommt ebenfalls das Feld (Maus geht dort auch).
 */
export const istTouchGeraet = (groberZeiger: boolean, schmal: boolean): boolean => groberZeiger || schmal;

/** YYYY-MM-DD → DD.MM.YYYY ohne Zeitzonen-Umweg; alles andere unveraendert. */
export const formatiereDatum = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

/**
 * Getipptes Datum (Umschalter „Eintippen" im Formular): die Seite setzt die
 * Punkte selbst. Tag und Monat haben zwei Ziffern, die dritte Ziffer beginnt
 * den naechsten Teil; ein Trenner (Punkt, Komma, Strich, Schraegstrich,
 * Leerzeichen) schliesst einen Teil ab. Das Jahr hat hoechstens vier
 * Ziffern, alles danach faellt weg. Ein eingefuegtes ISO-Datum wird
 * umgedreht.
 *
 * Fuehrende Null nur am ENDE der Eingabe („1." → „01."): wer mitten im Feld
 * aus „16.03." die 6 loescht, behaelt „1.03." — jede Umschreibung setzt die
 * Schreibmarke ans Ende, und die naechste Ziffer landete hinter dem Jahr
 * (Review 01.10.: daraus wurde still der 01.03.). Die Maske ist idempotent;
 * ihr eigenes Ergebnis schreibt sie nie um.
 */
export const tippDatumMaske = (eingabe: string): string => {
  const iso = /^\s*(\d{4})-(\d{2})-(\d{2})\s*$/.exec(eingabe);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;
  const zeichen = [...eingabe.trimStart()];
  const teile = [''];
  zeichen.forEach((z, pos) => {
    const i = teile.length - 1;
    if (/\d/.test(z)) {
      if (teile[i].length < (i < 2 ? 2 : 4)) teile[i] += z;
      else if (i < 2) teile.push(z);
    } else if (/[.,\-/\s]/.test(z) && i < 2) {
      const zifferFolgt = zeichen.slice(pos + 1).some((r) => /\d/.test(r));
      // Ein leerer Teil bleibt stehen, wenn dahinter noch Ziffern kommen: wer
      // in „16.03.1985" den Tag ganz loescht, um ihn neu zu tippen, behaelt „.03.1985".
      if (!teile[i] && !zifferFolgt) return;
      if (!zifferFolgt) teile[i] = teile[i].padStart(2, '0');
      teile.push('');
    }
  });
  return teile.join('.');
};

/**
 * Was beim Tippen ins Formular geht: ein vollstaendiges Datum (Tag und Monat
 * ein- oder zweistellig, Jahr vierstellig) → ISO, sonst der maskierte Text
 * selbst. Den Text statt '' zu speichern heisst, pruefeFormular meldet
 * „kein gültiges Datum" statt „Bitte eingeben". Ob es den Tag gibt (31.02.),
 * prueft pruefeFormular.
 */
export const tippDatumWert = (eingabe: string): string => {
  const text = tippDatumMaske(eingabe);
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(text);
  return m ? `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}` : text;
};

/** Dauer fuer die Begruessung: „ca. 15 Minuten"; ohne Angabe leer. */
export const dauerText = (dauerMin: number | null | undefined): string =>
  dauerMin && dauerMin > 0 ? `ca. ${dauerMin} Minuten` : '';
